import { useEffect, useState } from "react";
import filteringProps from "../filtering/jobcolors.json";
import sortingProps from "../sorting/jobcolors.json";
import { DataTable, DataTableField } from "../../../../components/data/DataTable";
import DataTableActiveToggle from "../../../../components/data/DataTableActiveToggle";
import { API_ROUTES } from '../../../../core/Constants';
import { loadJobColors, setPage, toPrettyDate, useHub } from '../../../../core/Global';
import Modal from "../../../../components/base/Modal";
import { toastSuccess } from "../../../../components/base/Toast";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import TextBox from "../../../../components/form/TextBox";
import JobColorItem from "../models/JobColorItem";
import { canDeleteColors, canManageColors, normalizeHex } from "../../../../components/color/ColorPalette";

const FALLBACK_COLOR = "#3B82F6";

export default function JobColorsList() {

  const hub = useHub();

  const [createModal, createModalSet] = useState<boolean>(false);
  const [editModal, editModalSet] = useState<boolean>(false);
  const [selectedItem, selectedItemSet] = useState<JobColorItem>();

  const [draftColor, draftColorSet] = useState<string>(FALLBACK_COLOR);
  const [draftText, draftTextSet] = useState<string>(FALLBACK_COLOR);

  const [refresh, refreshSet] = useState<boolean>();
  const [clear, clearSet] = useState<boolean>();

  useEffect(() => {
    setPage("Job Colors", "CRM", "Base Data");
  }, [])

  useEffect(() => {
    if (refresh) setTimeout(() => refreshSet(false), 1000);
    if (clear) setTimeout(() => clearSet(false), 1000);
  }, [refresh, clear])

  const canManage = canManageColors();

  const setDraft = (value: string) => {
    draftColorSet(value);
    draftTextSet(value.toUpperCase());
  }

  const openCreate = () => {
    setDraft(FALLBACK_COLOR);
    createModalSet(true);
  }

  const edit = (data: JobColorItem) => {
    selectedItemSet(data);
    setDraft(normalizeHex(data.color) ?? FALLBACK_COLOR);
    editModalSet(true);
  }

  /** Colour swatch + the account's own name for it. */
  const NameItem = (data: JobColorItem) => (
    <FlexRow>
      <span style={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: data.color,
        border: "1px solid var(--bs-border-color)",
        display: "inline-block"
      }} />
      <div className="ms-3 fw-bold">
        {data.name}
      </div>
      {
        canManage &&
        <a href="#" className="ms-2"
          onClick={e => {
            e.preventDefault();
            edit(data);
          }}>
          <Icon icon="edit" size={12} />
        </a>
      }
    </FlexRow>
  )

  const ColorCode = (data: JobColorItem) => (
    <div className="fs-7 text-gray-700" style={{ letterSpacing: ".04em" }}>
      {data.color?.toUpperCase()}
    </div>
  )

  const CreatedItem = (data: JobColorItem) => (
    <div>
      <div className='d-flex align-items-center'>
        {toPrettyDate(data.createdAt)}
      </div>
      {
        data.creatorUser &&
        <FlexRow className='fs-8 text-gray-600 mt-1'>
          <Icon icon="user" className="me-2" size={10} type="solid" />
          <div className="fs-8">
            By: {data.creatorUser.fullName}
          </div>
        </FlexRow>
      }
    </div>
  )

  /** Native picker for choosing, hex field for pasting a brand colour. Kept in sync both ways. */
  const ColorField = () => (
    <FlexRow className="mb-5">
      <input type="color" value={draftColor} className="me-3"
        style={{
          width: 46,
          height: 38,
          padding: 0,
          border: "1px solid var(--bs-border-color)",
          borderRadius: 9,
          background: "transparent",
          cursor: "pointer"
        }}
        onChange={e => setDraft(e.target.value)} />
      <input type="text" value={draftText} maxLength={7} spellCheck={false}
        className="form-control form-control-sm"
        style={{ width: 120, textTransform: "uppercase", letterSpacing: ".04em" }}
        onChange={e => {
          draftTextSet(e.target.value);
          const hex = normalizeHex(e.target.value);
          if (hex) draftColorSet(hex);
        }} />
    </FlexRow>
  )

  return (
    <>
      <DataTable
        objectName="Job Color"
        apiRoute={API_ROUTES.JobColors}
        filteringProps={filteringProps}
        sortingProps={sortingProps}
        // Palette order, exactly what the colour pickers show. Without this the table asks for
        // descending and the page reads back-to-front compared to every picker.
        ascending
        reorder={canManage}
        onReorder={ids => {
          hub.Post(API_ROUTES.JobColors + "reorder", ids, res => {
            // Repaint the pickers so a drag here is visible the next time someone paints a job.
            if (res.success) loadJobColors(true);
          })
        }}
        addCustom={canManage ? openCreate : undefined}
        edit={canManage} editCustom={edit}
        delete={canDeleteColors()}
        refresh={refresh}>
        <DataTableField title="" width={50}>
          {/* The shared toggle every other base-data page uses: controlled, disabled while the
              request is in flight, and it takes its new state from the response. The callback
              repaints the pickers, which an inactive colour drops out of. */}
          <DataTableActiveToggle route={API_ROUTES.JobColors} callback={() => loadJobColors(true)} />
        </DataTableField>
        <DataTableField title="Name">
          <NameItem />
        </DataTableField>
        <DataTableField title="Color Code">
          <ColorCode />
        </DataTableField>
        <DataTableField title="Created">
          <CreatedItem />
        </DataTableField>
      </DataTable>

      {
        createModal &&
        <Modal id="modal-add-jobcolor"
          title="Create New Job Color"
          buttonText="Add Job Color"
          staticBackdrop visible open
          onClose={() => createModalSet(false)}
          onOpen={() => { $("#txt-addjobcolor-name").focus(); }}
          onSubmit={(body, callback) => {

            if (!body.name || body.name.length < 2) {
              window.showFormError("name", "Please write a valid name!", false, "modal-add-jobcolor");
              if (callback) callback();
              return;
            }

            hub.Post(API_ROUTES.JobColors, { ...body, color: draftColor }, res => {
              if (res.success) {
                refreshSet(true);
                clearSet(true);
                loadJobColors(true);
                toastSuccess("Job color has been added successfully!");
              }
              if (callback) callback(res);
            })

          }}>
          <div>
            <ColorField />
            <TextBox id="txt-addjobcolor-name" name="name" block label="Name" labelUp
              placeholder="e.g. Problem Jobs" clear={clear} />
            <div className="fs-8 text-muted mt-2">
              Name it however your team talks about it — it doesn't have to be the color's name.
            </div>
          </div>
        </Modal>
      }

      {
        editModal && selectedItem &&
        <Modal id="modal-edit-jobcolor"
          title="Edit Job Color"
          buttonText="Update Job Color"
          staticBackdrop open visible
          onClose={() => {
            editModalSet(false);
            selectedItemSet(undefined);
          }}
          onOpen={() => { $("#txt-editjobcolor-name").focus(); }}
          onSubmit={(body, callback) => {

            if (!body.name || body.name.length < 2) {
              window.showFormError("name", "Please write a valid name!", false, "modal-edit-jobcolor");
              if (callback) callback();
              return;
            }

            hub.Put(API_ROUTES.JobColors + selectedItem.id, { ...body, color: draftColor }, res => {
              if (res.success) {
                refreshSet(true);
                loadJobColors(true);
                toastSuccess("Job color has been updated successfully!");
              }
              if (callback) callback(res);
            })

          }}>
          <div>
            <ColorField />
            <TextBox id="txt-editjobcolor-name" name="name" block label="Name" labelUp
              defaultValue={selectedItem.name} />
            <div className="fs-8 text-muted mt-2">
              Changing the color repaints every job currently using it.
            </div>
          </div>
        </Modal>
      }
    </>
  );
}
